import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  FormGroup,
  Label,
} from 'reactstrap'
import { useEffect, useRef, useState } from 'react'
import './watchInterview.css'
import AxiosInstance from 'utils/AxiosInstance'

function WatchInterview({ interviewData }) {
  const videoRef = useRef(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [questions, setQuestions] = useState([])
  const [questionsFetched, setQuestionsFetched] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [editedScores, setEditedScores] = useState({})
  const [saving, setSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  const data = interviewData.timestamps
    ? JSON.parse(interviewData.timestamps)
    : []
  const parsedData = JSON.parse(data)

  // Convert time strings (e.g. "00:28") into total seconds
  const getSeconds = (timeStr) => {
    const [min, sec] = timeStr.split(':').map(Number)
    return min * 60 + sec
  }

  useEffect(() => {
    if (questionsFetched) return

    AxiosInstance.get(
      `/recruitment/ai-interview/answered/questions/${interviewData?.id}?ids=${
        parsedData && parsedData.length
          ? parsedData.map((el) => el.id).join(',')
          : ''
      }`
    ).then((data) => {
      setQuestionsFetched(true)
      setQuestions(data)
    })
  }, [parsedData, questions.length, questionsFetched, interviewData?.id])

  // Watch video time updates
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime)
    }

    video.addEventListener('timeupdate', handleTimeUpdate)
    return () => video.removeEventListener('timeupdate', handleTimeUpdate)
  }, [])

  // Find the current active timestamp index
  let activeIndex = parsedData.findIndex((timestamp, index) => {
    const current = getSeconds(timestamp.time)
    const next =
      index + 1 < parsedData.length
        ? getSeconds(parsedData[index + 1].time)
        : Infinity
    return currentTime >= current && currentTime < next
  })

  // Seek video when a timestamp is clicked
  const handleSeek = (timeStr) => {
    const seconds = getSeconds(timeStr)
    if (videoRef.current) {
      videoRef.current.currentTime = seconds
    }
  }

  if (activeIndex === -1) {
    activeIndex = 0
  }

  // Get score color based on score value
  const getScoreColor = (score) => {
    if (score >= 8) return 'success'
    if (score >= 6) return 'warning'
    return 'danger'
  }

  // Get score badge variant
  const getScoreBadgeClass = (score) => {
    if (score >= 8) return 'badge-success'
    if (score >= 6) return 'badge-warning'
    return 'badge-danger'
  }

  // Handle score change
  const handleScoreChange = (questionId, newScore) => {
    const score = Math.max(0, Math.min(10, parseFloat(newScore) || 0))
    setEditedScores((prev) => ({ ...prev, [questionId]: score }))
    setHasChanges(true)
  }

  // Get current score (edited or original)
  const getCurrentScore = (question) => {
    return editedScores[question.id] !== undefined
      ? editedScores[question.id]
      : question.score
  }

  const toggleEditMode = () => {
    if (editMode) {
      setEditedScores({})
      setHasChanges(false)
    }
    setEditMode(!editMode)
  }

  // Save changes
  const saveChanges = async () => {
    if (!hasChanges) return

    setSaving(true)
    try {
      const updates = Object.entries(editedScores).map(
        ([questionId, score]) => ({
          questionId,
          score,
        })
      )

      await AxiosInstance.put(`/recruitment/ai-interview/update-scores`, {
        interviewId: interviewData.id,
        scores: updates,
      })

      setQuestions((prev) =>
        prev.map((q) => ({
          ...q,
          score:
            editedScores[q.id] !== undefined ? editedScores[q.id] : q.score,
        }))
      )

      setEditedScores({})
      setHasChanges(false)
      setEditMode(false)
    } catch (error) {
      console.error('Failed to save score changes:', error)
      alert('Failed to save changes. Please try again.')
    }
    setSaving(false)
  }

  const cancelChanges = () => {
    setEditedScores({})
    setHasChanges(false)
    setEditMode(false)
  }

  return (
    <div>
      <div className='d-flex align-items-top justify-content-between mb-4'>
        <Card style={{ width: '65%' }}>
          <video
            ref={videoRef}
            controls
            style={{ width: '100%', borderRadius: '0.5rem' }}
            src={interviewData.link}
          >
            Your browser does not support the video tag.
          </video>
        </Card>

        <Card style={{ width: '32%' }}>
          <CardHeader className='title'>Chapters</CardHeader>
          <CardBody className='p-0'>
            {parsedData && parsedData.length
              ? parsedData.map((timestamp, index) => (
                  <div
                    key={index}
                    role='button'
                    onClick={() => handleSeek(timestamp.time)}
                    className={`p-4 d-flex flex-column border border-top-0 timestamp ${
                      index === activeIndex ? 'active-timestamp' : ''
                    }`}
                  >
                    <span className='mb-1 font-weight-bold'>
                      {
                        questions?.find((q) => q?.id === timestamp?.id)
                          ?.question
                      }
                    </span>
                    <span>{timestamp.time}</span>
                  </div>
                ))
              : null}
          </CardBody>
        </Card>
      </div>

      {/* Interview Transcript Section */}
      <Card>
        <CardHeader className='title d-flex align-items-center justify-content-between'>
          <div className='d-flex align-items-center'>
            <span className='mr-3'>Interview Transcript & AI Assessment</span>
          </div>
          <div className='d-flex align-items-center'>
            <small className='text-muted mr-3'>
              {questions?.length || 0} Questions Answered
            </small>
            {!editMode ? (
              <Button
                color='outline-primary'
                size='sm'
                onClick={toggleEditMode}
                className='score-edit-btn'
              >
                <i className='fas fa-edit mr-1'></i>
                Edit Scores
              </Button>
            ) : (
              <div className='d-flex align-items-center edit-controls'>
                {hasChanges && (
                  <span className='badge badge-warning mr-2 animate-bounce'>
                    <i className='fas fa-exclamation-triangle mr-1'></i>
                    Unsaved Changes
                  </span>
                )}
                <Button
                  color='success'
                  size='sm'
                  onClick={saveChanges}
                  disabled={!hasChanges || saving}
                  className='mr-2'
                >
                  {saving ? (
                    <>
                      <i className='fas fa-spinner fa-spin mr-1'></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className='fas fa-save mr-1'></i>
                      Save Changes
                    </>
                  )}
                </Button>
                <Button
                  color='outline-secondary'
                  size='sm'
                  onClick={cancelChanges}
                  disabled={saving}
                >
                  <i className='fas fa-times mr-1'></i>
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardBody className='p-0'>
          {questions && questions.length > 0 ? (
            questions.map((question, index) => (
              <div key={question.id} className='transcript-item border-bottom'>
                <div className='p-4'>
                  {/* Question Header */}
                  <div className='d-flex align-items-start justify-content-between mb-3'>
                    <div className='question-header flex-grow-1'>
                      <div className='d-flex align-items-center mb-2'>
                        <span className='question-number badge badge-primary mr-2'>
                          Q{index + 1}
                        </span>
                        <h6 className='mb-0 font-weight-bold text-dark'>
                          {question.question}
                        </h6>
                      </div>
                    </div>
                    <div className='ml-3 score-section'>
                      {!editMode ? (
                        <span
                          className={`badge ${getScoreBadgeClass(
                            getCurrentScore(question)
                          )} px-3 py-2 score-badge`}
                        >
                          <i className='fas fa-star mr-1'></i>
                          {getCurrentScore(question)}/10
                        </span>
                      ) : (
                        <div className='score-editor d-flex align-items-center'>
                          <FormGroup className='mb-0 mr-2'>
                            <Label className='mb-0 mr-2 text-muted small font-weight-bold'>
                              SCORE:
                            </Label>
                          </FormGroup>
                          <div className='score-input-wrapper position-relative'>
                            <Input
                              type='number'
                              min='0'
                              max='10'
                              step='1'
                              value={getCurrentScore(question)}
                              onChange={(e) =>
                                handleScoreChange(question.id, e.target.value)
                              }
                              className='score-input text-center font-weight-bold'
                              style={{ width: '80px' }}
                            />
                            <span className='score-suffix'>/10</span>
                          </div>
                          <div className='score-indicator ml-2'>
                            <span
                              className={`badge ${getScoreBadgeClass(
                                getCurrentScore(question)
                              )} score-preview`}
                            >
                              <i className='fas fa-star mr-1'></i>
                              {getCurrentScore(question)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Answer Section */}
                  <div className='answer-section mb-3'>
                    <div className='d-flex align-items-center mb-2'>
                      <i className='fas fa-user text-primary mr-2'></i>
                      <small className='text-muted font-weight-bold'>
                        CANDIDATE ANSWER:
                      </small>
                    </div>
                    <div className='answer-content bg-light p-3 rounded'>
                      <p
                        className='mb-0 text-dark'
                        style={{ lineHeight: '1.6' }}
                      >
                        {question.answer || 'No answer provided'}
                      </p>
                    </div>
                  </div>

                  {/* AI Assessment */}
                  {question.reason && (
                    <div className='ai-assessment'>
                      <div className='d-flex align-items-center mb-2'>
                        <i className='fas fa-robot text-info mr-2'></i>
                        <small className='text-muted font-weight-bold'>
                          AI ASSESSMENT:
                        </small>
                      </div>
                      <div
                        className={`assessment-content border-left border-${getScoreColor(
                          question.score
                        )} pl-3`}
                      >
                        <p className='mb-0' style={{ lineHeight: '1.5' }}>
                          {question.reason}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className='p-4 text-center text-muted'>
              <i className='fas fa-comment-slash fa-3x mb-3'></i>
              <h6>No transcript available</h6>
              <small>
                Interview questions and answers will appear here once processed.
              </small>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

export default WatchInterview
